import { useParams } from 'react-router-dom'
import alternativePages from '@/data/alternativePages.json'
import { Header } from '@/components/landing/Header'
import { Footer } from '@/components/landing/Footer'
import { MessageCircleQuestion, Trophy, CheckCircle, XCircle, ChevronRight } from 'lucide-react'
import { motion } from 'framer-motion'
import { useState } from 'react'
import { FeatureCard } from '@/components/landing/FeatureCard'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { Breadcrumb } from '@/components/Breadcrumb'
import { toAbsoluteUrl } from '@/lib/seo'

const AlternativePSEOPage = () => {
  const { slug, toolSlug } = useParams<{ slug?: string; toolSlug?: string }>()
  
  // Get the actual tool slug from either URL format
  const getDataSlug = () => {
    // If we're using /alternative-[tool] format, slug will contain the tool
    if (slug) return `alternative-${slug}`
    
    // If we're using /alternatives/[tool] format, toolSlug will contain the tool
    if (toolSlug) return `alternative-${toolSlug}`
    
    return null
  }
  
  const dataSlug = getDataSlug()
  const data = dataSlug ? alternativePages[dataSlug] : null
  const [logoError, setLogoError] = useState(false)

  // Extract tool name from slug
  const extractToolName = () => {
    const toolNameSlug = slug || toolSlug || ''
    if (!toolNameSlug) return ''
    
    return toolNameSlug.charAt(0).toUpperCase() + toolNameSlug.slice(1)
  }

  const toolName = extractToolName()

  // Generate breadcrumbs
  const getBreadcrumbs = () => {
    return [
      { name: "Home", url: "/" },
      { name: "Alternatives", url: "/alternatives" },
      { name: toolName, url: `/alternatives/${slug || toolSlug}` }
    ]
  }

  const breadcrumbs = getBreadcrumbs()

  // Create JSON-LD for breadcrumb schema
  const breadcrumbSchemaData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": toAbsoluteUrl(crumb.url)
    }))
  }

  // Create FAQ schema
  const faqSchemaData = data ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": data.faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null

  // If the page is not found, let's add a helpful debug message
  if (!data) {
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white">
        <div className="text-center p-10 bg-white rounded-2xl shadow-lg border border-gray-100 max-w-2xl">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Page not found 🚫</h1>
          <p className="text-gray-600 mb-6">The alternative page you're looking for doesn't exist or has been removed.</p>
          <div className="text-left bg-gray-50 p-4 rounded-lg text-sm text-gray-700 mb-6">
            <p><strong>Looking for:</strong> {dataSlug}</p>
            <p><strong>Available pages:</strong></p>
            <ul className="list-disc list-inside mt-2">
              {Object.keys(alternativePages).map(key => (
                <li key={key}>{key} (<Link to={`/alternative-${key.replace('alternative-', '')}`} className="text-[#866BFF] hover:underline">view</Link>)</li>
              ))}
            </ul>
          </div>
          <Link to="/" className="inline-block bg-[#866BFF] text-white font-semibold px-6 py-3 rounded-full hover:bg-[#7556f0] shadow-md transition">
            Back to Home
          </Link>
        </div>
      </div>
    )
  }

  const {
    intro,
    advantages,
    testimonials,
    features_comparison,
    pricing_comparison,
    faq,
    cta,
    related,
    user_complaints,
  } = data

  return (
    <div className="min-h-screen w-full mx-auto relative overflow-hidden bg-gradient-to-b from-gray-50 to-white">
      <Helmet>
        <title>{data.metaTitle ? data.metaTitle.replace(/\d{4}/, new Date().getFullYear().toString()) : `${toolName} Alternative ${new Date().getFullYear()} - Feedbask`}</title>
        <meta name="description" content={data.metaDescription} />

        {/* Dynamic canonical tag for this specific page */}
        <link rel="canonical" href={toAbsoluteUrl(`/alternatives/${slug || toolSlug}`)} />

        {/* Hreflang tags for international SEO */}
        <link rel="alternate" hrefLang="en" href={toAbsoluteUrl(`/alternatives/${slug || toolSlug}`)} />
        <link rel="alternate" hrefLang="x-default" href={toAbsoluteUrl(`/alternatives/${slug || toolSlug}`)} />

        {/* Open Graph tags for social sharing */}
        <meta property="og:title" content={data.metaTitle ? data.metaTitle.replace(/\d{4}/, new Date().getFullYear().toString()) : `${toolName} Alternative ${new Date().getFullYear()} - Feedbask`} />
        <meta property="og:description" content={data.metaDescription} />
        <meta property="og:url" content={toAbsoluteUrl(`/alternatives/${slug || toolSlug}`)} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={toAbsoluteUrl('/og-image.png')} />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@feedbask_com" />
        <meta name="twitter:title" content={data.metaTitle ? data.metaTitle.replace(/\d{4}/, new Date().getFullYear().toString()) : `${toolName} Alternative ${new Date().getFullYear()} - Feedbask`} />
        <meta name="twitter:description" content={data.metaDescription} />
        <meta name="twitter:url" content={toAbsoluteUrl(`/alternatives/${slug || toolSlug}`)} />
        <meta name="twitter:image" content={toAbsoluteUrl('/og-image.png')} />

        {/* Breadcrumb structured data */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchemaData)}
        </script>

        {/* FAQ structured data */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchemaData)}
        </script>

        {/* Article structured data for comparison page */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": `Best alternative to ${toolName} in ${new Date().getFullYear()}`,
            "description": data.metaDescription,
            "author": {
              "@type": "Organization",
              "name": "Feedbask"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Feedbask",
              "logo": {
                "@type": "ImageObject",
                "url": toAbsoluteUrl('/images/logo_feedbask.png')
              }
            },
            "datePublished": "2024-01-01",
            "dateModified": new Date().toISOString().split('T')[0]
          })}
        </script>
      </Helmet>

      {/* Background Blur */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10">
        <div className="absolute top-24 left-10 w-64 h-64 rounded-full bg-[#866BFF] opacity-30 blur-3xl"></div>
        <div className="absolute bottom-32 right-10 w-72 h-72 rounded-full bg-indigo-100 opacity-30 blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl px-4 mx-auto">
        <Header />

        {/* Breadcrumb */}
        <Breadcrumb items={breadcrumbs} />

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="py-24 flex flex-col md:flex-row items-center justify-between gap-10"
        >
          <div className="md:w-1/2 text-center md:text-left">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 leading-tight">
              Best alternative to{" "}
              <span className="bg-gradient-to-r from-purple-500 to-indigo-600 text-transparent bg-clip-text">
                {toolName}
              </span>{" "}
              in {new Date().getFullYear()}
            </h1>
            <h2 className="text-2xl md:text-3xl mt-4 font-semibold text-gray-700">
              Looking for an alternative to {toolName}?
            </h2>
            <p className="mt-6 text-gray-600 text-lg">{intro}</p>
            <div className="flex flex-col sm:flex-row gap-4 mt-6 justify-center md:justify-start">
              <a
                href="/auth"
                className="inline-block bg-[#866BFF] text-white font-semibold px-6 py-3 rounded-full hover:bg-[#7556f0] shadow-md transition-transform transform hover:scale-105"
              >
                Try Feedbask today
              </a>
              <a
                href="#features"
                className="inline-block border border-[#866BFF] text-[#866BFF] font-semibold px-6 py-3 rounded-full hover:bg-gray-50 transition-transform transform hover:scale-105"
              >
                Compare Features
              </a>
            </div>
          </div>
          <div className="md:w-1/2 relative flex items-center justify-center">
            <div className="relative w-full">
              <div className="flex items-center justify-center gap-6">
                {/* Logo du competitor (plus petit) */}
                <div className="mr-8 opacity-70">
                    {(() => {
                      const text = `${toolName}`;
                      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='180' height='60'>\n<rect width='100%' height='100%' rx='12' ry='12' fill='#EEF2FF'/>\n<text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#4F46E5' font-family='Inter, ui-sans-serif' font-size='22'>${text}</text>\n</svg>`;
                      const fallback = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
                      return (
                        <img
                          src={data.logo || fallback}
                          alt={`${toolName} logo`}
                          className="object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = fallback;
                            setLogoError(true);
                          }}
                        />
                      );
                    })()}
                </div>
                {(logoError || !data.logo) && (
                  <div className="flex items-center gap-4">
                    <span className="text-[#4F46E5] text-3xl" aria-hidden>
                      →
                    </span>
                    <img
                      src="/images/logo_feedbask.png"
                      alt="Feedbask logo"
                      className="h-12 w-auto object-contain"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.section>

       

        {/* Why Feedbask is better */}
        <section className="py-16" id="features">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Get so much more than task management with Feedbask
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-4 px-6 text-left text-gray-500 font-medium">Category</th>
                  <th className="py-4 px-6 text-center text-gray-900 font-semibold">Feedbask</th>
                  <th className="py-4 px-6 text-center text-gray-900 font-semibold">{toolName}</th>
                </tr>
              </thead>
              <tbody>
                {features_comparison.map((feature, index) => (
                  <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="py-4 px-6 text-gray-800">{feature.name}</td>
                    <td className="py-4 px-6 text-center">
                      {feature.feedbask ? (
                        <CheckCircle className="inline text-green-500 w-5 h-5" />
                      ) : (
                        <XCircle className="inline text-red-500 w-5 h-5" />
                      )}
                    </td>
                    <td className="py-4 px-6 text-center">
                      {feature.competitor ? (
                        <CheckCircle className="inline text-green-500 w-5 h-5" />
                      ) : (
                        <XCircle className="inline text-red-500 w-5 h-5" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* User Complaints */}
        {user_complaints && user_complaints.length > 0 && (
          <section className="py-20 bg-gray-50 rounded-3xl my-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What {toolName} clients don't like
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Real feedback from users who've experienced frustrations with {toolName}
              </p>
            </div>
            
            <div className={`${user_complaints.length < 3 ? 'flex flex-wrap justify-center gap-8 max-w-4xl' : 'grid md:grid-cols-3 gap-8 max-w-6xl'} mx-auto px-4`}>
              {user_complaints.map((complaint, index) => (
                <div key={index} className={`bg-white rounded-xl shadow-md overflow-hidden ${user_complaints.length < 3 ? 'w-full md:w-[45%]' : ''}`}>
                  {/* Image section */}
                  {/* <div className="h-48 overflow-hidden">
                    <img 
                      src={complaint.image} 
                      alt={`${toolName} user complaint`} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = "/images/complaints/default-complaint.png";
                      }}
                    />
                  </div> */}
                  
                  {/* Content section */}
                  <div className="p-5">
                    <div className="flex items-center mb-3">
                    
                    </div>
                    
                    <p className="text-gray-700 italic mb-3">"{complaint.text}"</p>
                    
                    <div className={`flex items-center mt-4 ${complaint?.author && complaint?.source ? 'justify-between' : complaint?.source ? 'justify-end' : 'justify-start'}`}>
                      {complaint?.author && (
                        <p className="text-sm text-gray-600">— {complaint.author}</p>
                      )}
                      {complaint?.source && (
                        <a
                          href={complaint.source}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#866BFF] font-medium hover:underline inline-flex items-center"
                        >
                          Read source <span className="ml-1">→</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Key Advantages */}
        <section className="py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900">Why you will love Feedbask</h2>
          </div>
          
          <div className="space-y-24">
            {advantages.slice(0, 4).map((advantage, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 md:gap-16 items-center`}>
                <div className="md:w-1/2">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">{advantage.title}</h3>
                  <p className="text-gray-600 text-lg">{advantage.description}</p>
                </div>
                <div className="md:w-1/2">
                  <div className="aspect-video rounded-xl shadow-md flex items-center justify-center bg-white overflow-hidden">
                    <img
                      src={advantage.imageUrl}
                      alt={advantage.title}
                      className="h-full w-auto object-contain"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

   
     

        {/* Pricing comparison */}
        {/* <section className="py-16" id="pricing">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Pricing comparison</h2>
            <p className="text-gray-600">See how Feedbask offers more value for your money</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Feedbask</h3>
                <div className="text-3xl font-bold text-gray-900 mt-2">${pricing_comparison.feedbask.price}<span className="text-lg text-gray-500">/mo</span></div>
                <p className="text-gray-500 text-sm">{pricing_comparison.feedbask.billing}</p>
              </div>
              
              <ul className="space-y-3">
                {pricing_comparison.feedbask.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="text-green-500 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-8 text-center">
                <a
                  href="/auth"
                  className="inline-block w-full bg-[#866BFF] text-white font-semibold px-6 py-3 rounded-full hover:bg-[#7556f0] shadow-md transition"
                >
                  Try for free
                </a>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">{toolName}</h3>
                <div className="text-3xl font-bold text-gray-900 mt-2">${pricing_comparison.competitor.price}<span className="text-lg text-gray-500">/mo</span></div>
                <p className="text-gray-500 text-sm">{pricing_comparison.competitor.billing}</p>
              </div>
              
              <ul className="space-y-3">
                {pricing_comparison.competitor.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="text-green-500 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-8 text-center">
                <a
                  href="/auth"
                  className="inline-block w-full border border-gray-300 text-gray-700 font-semibold px-6 py-3 rounded-full hover:bg-gray-50 transition"
                >
                  Try Feedbask instead
                </a>
              </div>
            </div>
          </div>
        </section> */}

        {/* Testimonials */}
        {/* <section className="py-16 bg-gray-50 rounded-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">What our customers say</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto px-4">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-center mb-4">
                  <img src={testimonial.avatarUrl} alt={testimonial.name} className="w-10 h-10 rounded-full mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-gray-700">{testimonial.content}</p>
              </div>
            ))}
          </div>
        </section> */}

        {/* FAQ */}
        <section className="py-16" id="faq">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Common Questions</h2>
            <p className="text-gray-600 mt-2">Get answers to frequently asked questions about Feedbask vs {toolName}</p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            {faq.map((item, index) => (
              <div key={index} className="mb-8 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-start gap-4">
                  <MessageCircleQuestion className="w-6 h-6 mt-1 text-[#866BFF] flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{item.question}</h3>
                    <p className="mt-2 text-gray-700">{item.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {related?.length > 0 && (
          <section className="py-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">See other alternatives</h2>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
              {related.map((item, index) => {
                const relatedToolSlug = item.slug.replace(/^alternative-/, '')
                return (
                  <Link
                    key={index}
                    to={`/alternatives/${relatedToolSlug}`}
                    className="block bg-white rounded-xl border border-gray-200 shadow-sm px-6 py-4 text-center hover:shadow-md transition"
                  >
                    <p className="text-lg font-semibold text-[#866BFF]">{item.label}</p>
                  </Link>
                )
              })}
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="text-center py-20 bg-[#866BFF] text-white rounded-2xl shadow-lg my-16">
          <h2 className="text-3xl font-bold mb-4">{cta}</h2>
          <a
            href="/auth"
            className="inline-block bg-white text-[#866BFF] font-semibold px-8 py-3 mt-4 rounded-full hover:bg-gray-100 transition"
          >
            Get started for free
          </a>
        </section>

        <Footer />
      </div>
    </div>
  )
}

export default AlternativePSEOPage 